import { useEffect, useRef, useState } from 'react';
import {
  fetchAnswer, localAsk, suggestQuestions,
  type AiConfig, type AskResult, type AskTurn, type ResolvedRef, type TenantBundle,
} from '@ce/core';
import { btn, divider, emptyBox, kicker, portrait, settle, t } from '../styles';
import type { ScreenProps } from './types';

/** One line under the answer, saying what kind of answer it actually is. */
function statusLine(bundle: TenantBundle, result: AskResult): string {
  if (result.reason === 'rate-limited') {
    const wait = result.retryAfter ? ` (about ${result.retryAfter}s)` : '';
    return bundle.strings.searchRateLimited + wait;
  }
  if (result.reason === 'unconfigured') return bundle.strings.searchUnconfigured;
  if (result.reason === 'no-endpoint') return bundle.strings.searchNoEndpoint;
  if (result.reason === 'bad-format') return bundle.strings.searchBadFormat;
  if (result.fallback) return bundle.strings.searchError;
  return bundle.strings.searchDisclaimer;
}

const KIND_LABEL: Record<ResolvedRef['kind'], string> = {
  session: 'Session',
  talent: 'Guest',
  exhibitor: 'Booth',
  event: 'Special event',
};

/**
 * Ask — natural-language search over the event.
 *
 * The answer prose comes from the model; the cards beneath it are resolved from
 * the bundle, so anything the model invented simply does not appear. Tapping a
 * card goes to the real detail page for that thing.
 */
export function Search({ bundle, state, dispatch, config }: ScreenProps) {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<AskResult | null>(null);
  const [loading, setLoading] = useState(false);
  // Kept so a follow-up like "what about Sunday?" resolves against the
  // previous answer. The endpoint accepts up to 12 turns.
  const [history, setHistory] = useState<AskTurn[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const suggestions = suggestQuestions(bundle, state.interests, state.day);

  // The AI endpoint is its own service, separate from the content CMS.
  const ai: AiConfig = {
    // The admin can switch the assistant off; search then runs on offline keyword matching.
    baseUrl: config.features.askAssistant ? (import.meta.env.VITE_AI_URL ?? '') : '',
    token: import.meta.env.VITE_AI_TOKEN || undefined,
  };

  useEffect(() => {
    inputRef.current?.focus();
    return () => abortRef.current?.abort();
  }, []);

  const ask = (question: string) => {
    const q = question.trim();
    if (!q) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setQuery(q);
    setLoading(true);
    // Show keyword matches immediately, then replace with the model's answer.
    setResult(localAsk(bundle, q));

    fetchAnswer(
      ai,
      bundle,
      { tenantId: bundle.tenantId, question: q, interests: state.interests },
      fetch,
      controller.signal,
      history,
    )
      .then((r) => {
        if (controller.signal.aborted) return;
        setResult(r);
        // Only a real answer joins the thread; a keyword fallback would poison
        // the context for the next question.
        if (!r.fallback) {
          setHistory((h) => [...h, { role: 'user', content: q }, { role: 'assistant', content: r.answer }]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  };

  const openRef = (ref: ResolvedRef) => {
    if (ref.kind === 'session') dispatch({ type: 'openSession', id: ref.id });
    else if (ref.kind === 'talent') dispatch({ type: 'openTalent', id: ref.id });
    else if (ref.kind === 'event') dispatch({ type: 'go', screen: 'events' });
    else {
      dispatch({ type: 'go', screen: 'hall' });
      dispatch({ type: 'setHallTab', tab: 'exhibitors' });
    }
  };

  return (
    <div className="stagger">
      <div
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', gap: 10, padding: '2px 14px',
          height: 46, borderRadius: 10, border: `1px solid ${divider}`,
          background: 'var(--color-surface)',
        }}
      >
        <div
          style={{
            flex: 'none', width: 14, height: 14, borderRadius: '50%',
            border: `1.5px solid ${t(0.45)}`,
          }}
        />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ask(query)}
          placeholder={bundle.strings.searchPlaceholder}
          aria-label={bundle.strings.searchPlaceholder}
          maxLength={300}
          style={{
            flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent',
            font: '400 14px/1.4 var(--font-body)', color: 'var(--color-text)',
          }}
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); setResult(null); inputRef.current?.focus(); }}
            aria-label="Clear"
            style={{
              flex: 'none', border: 0, background: 'transparent', cursor: 'pointer',
              color: t(0.45), fontSize: 15, lineHeight: 1,
            }}
          >
            ×
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => ask(query)}
        disabled={!query.trim() || loading}
        style={btn('primary', {
          width: '100%', height: 44, marginTop: 10, borderRadius: 10, fontSize: 14.5,
          opacity: !query.trim() || loading ? 0.5 : 1,
        })}
      >
        {loading ? 'Looking…' : 'Ask'}
      </button>

      {!result && (
        <>
          <p style={{ fontSize: 12.5, lineHeight: 1.6, color: t(0.55), margin: '20px 0 0' }}>
            {bundle.strings.searchIntro}
          </p>
          <div style={{ ...kicker(), margin: '22px 0 11px' }}>
            {bundle.strings.searchSuggestionsLabel}
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {suggestions.map((s, i) => (
              <button
                key={s}
                type="button"
                onClick={() => ask(s)}
                style={{
                  ...settle(i, 'sm'),
                  display: 'flex', alignItems: 'center', gap: 11, width: '100%',
                  textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                  border: `1px solid ${divider}`, background: 'transparent',
                  color: 'var(--color-text)', font: '400 13.5px/1.4 var(--font-body)',
                }}
              >
                <span
                  style={{
                    flex: 'none', width: 5, height: 5, borderRadius: '50%',
                    background: 'var(--color-accent)',
                  }}
                />
                {s}
              </button>
            ))}
          </div>
        </>
      )}

      {result && (
        <div style={{ marginTop: 20 }}>
          <div style={kicker(9.5, 'var(--color-accent-300)', 0.16)}>
            {loading ? 'Looking…' : 'Answer'}
          </div>

          <p
            style={{
              font: '400 14.5px/1.65 var(--font-body)', color: t(0.86),
              margin: '10px 0 0', textWrap: 'pretty',
            }}
          >
            {result.answer}
          </p>

          {result.refs.length > 0 && (
            <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
              {result.refs.map((ref) => (
                <button
                  key={`${ref.kind}-${ref.id}`}
                  type="button"
                  onClick={() => openRef(ref)}
                  style={{
                    display: 'flex', gap: 12, alignItems: 'center', width: '100%',
                    textAlign: 'left', padding: '11px 13px', borderRadius: 10, cursor: 'pointer',
                    border: 0, background: 'var(--color-surface)', color: 'var(--color-text)',
                    boxShadow: 'var(--shadow-sm)',
                  }}
                >
                  <div
                    style={{
                      flex: 'none', width: 40, height: 40, borderRadius: 8,
                      overflow: 'hidden', background: 'var(--color-neutral-900)',
                      display: 'grid', placeItems: 'center',
                    }}
                  >
                    {ref.imageUrl ? (
                      <div style={portrait(ref.imageUrl, 20)} />
                    ) : (
                      <div
                        style={{
                          width: 13, height: 13, borderRadius: '50%',
                          border: '1.5px solid var(--color-accent)',
                        }}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={kicker(9, t(0.42), 0.14)}>{KIND_LABEL[ref.kind]}</div>
                    <div style={{ font: '500 14px/1.25 var(--font-body)', marginTop: 5 }}>
                      {ref.title}
                    </div>
                    <div style={{ fontSize: 11.5, color: t(0.5), marginTop: 3 }}>{ref.meta}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {result.refs.length === 0 && result.fallback && (
            <div style={{ ...emptyBox, marginTop: 14 }}>{bundle.strings.searchEmpty}</div>
          )}

          <div style={{ fontSize: 10.5, lineHeight: 1.5, color: t(0.35), marginTop: 16 }}>
            {statusLine(bundle, result)}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              type="button"
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              style={btn('primary', { flex: 1, height: 42 })}
            >
              Ask a follow-up
            </button>
            <button
              type="button"
              onClick={() => {
                setResult(null); setQuery(''); setHistory([]); inputRef.current?.focus();
              }}
              style={btn('secondary', { height: 42 })}
            >
              Start over
            </button>
          </div>
        </div>
      )}
      <div style={{ height: 26 }} />
    </div>
  );
}
